import csvValue from "./csvValue.js";
import { isTransannularIgnored } from "./formatMechanismStep.js";

function orderMechanismSteps(data) {
    // Group by mechanism_id
    const groupedByMechanism = {};
    data.forEach(row => {
        if (!groupedByMechanism[row.mechanism_id]) {
            groupedByMechanism[row.mechanism_id] = [];
        }
        groupedByMechanism[row.mechanism_id].push(row);
    });

    const orderedData = [];

    for (const mechanism_id in groupedByMechanism) {
        const steps = groupedByMechanism[mechanism_id];

        // Group by mechanism_step_id
        const stepGroups = {};
        steps.forEach(row => {
            const step_id = row.mechanism_step_id;
            if (!stepGroups[step_id]) {
                stepGroups[step_id] = [];
            }
            stepGroups[step_id].push(row);
        });

        // Build mapping: step_id -> {substrates, products}
        const stepConnections = {};
        for (const step_id in stepGroups) {
            const group = stepGroups[step_id];
            const substrates = new Set();
            const products = new Set();

            group.forEach(r => {
                if (r.substrate_pers_id) substrates.add(r.substrate_pers_id);
                if (r.product_pers_id) products.add(r.product_pers_id);
            });

            stepConnections[step_id] = {
                substrates,
                products,
                data: group
            };
        }

        // Build graph: step_id -> [next_step_ids]
        const graph = {};
        const inDegree = {};

        for (const from_id in stepConnections) {
            graph[from_id] = [];
            inDegree[from_id] = 0;
        }

        for (const from_id in stepConnections) {
            const fromProducts = stepConnections[from_id].products;
            for (const to_id in stepConnections) {
                if (from_id === to_id) continue;
                const toSubstrates = stepConnections[to_id].substrates;
                for (const p of fromProducts) {
                    if (toSubstrates.has(p)) {
                        graph[from_id].push(to_id);
                        inDegree[to_id]++;
                        break;
                    }
                }
            }
        }

        // Topological sort (Kahn's algorithm)
        const queue = Object.keys(inDegree).filter(k => inDegree[k] === 0);
        const sortedStepIds = [];

        while (queue.length > 0) {
            const current = queue.shift();
            sortedStepIds.push(current);
            for (const neighbor of graph[current]) {
                inDegree[neighbor]--;
                if (inDegree[neighbor] === 0) {
                    queue.push(neighbor);
                }
            }
        }

        // Append sorted steps to orderedData
        sortedStepIds.forEach(step_id => {
            orderedData.push(...stepConnections[step_id].data);
        });
    }

    return orderedData;
}


// Collapse one (mechanism_id, mechanism_step_id) group into a single CSV line.
function stepGroupToCsvLine(same_mechanism){
    let enzymes_uniprot = new Set()
    let enzymes_genbank = new Set()
    let enzymes_marts = new Set()
    let substrate_names = new Set()
    let product_names = new Set()
    let substrate_smiles_set = new Set()
    let product_smiles_set = new Set()
    let reaction_type = ""
    let distance = ""
    let transannular = ""
    let evidence = ""
    let mechanism_marts_id = ""
    let substrate_pers_id_set = new Set()
    let product_pers_id_set = new Set()
    let publications = new Set()
    let substrate_stereochemistry_set = new Set()
    let product_stereochemistry_set = new Set()
    same_mechanism.forEach((step) =>  {
        enzymes_uniprot.add(step.uniprot_id)
        enzymes_genbank.add(step.genbank_id)
        enzymes_marts.add(step.enzyme_pers_id)
        substrate_names.add(step.substrate_name)
        substrate_smiles_set.add(step.substrate_smiles)
        substrate_pers_id_set.add(step.substrate_pers_id)
        product_pers_id_set.add(step.product_pers_id)
        product_names.add(step.product_name)
        product_smiles_set.add(step.product_smiles)
        substrate_stereochemistry_set.add(step.substrate_stereochemistry)
        product_stereochemistry_set.add(step.product_stereochemistry)
        reaction_type = csvValue(step.step_type)
        distance = csvValue(step.step_distance)
        // SQL NULL stays an empty cell; 1 / 0 become True / False. Cyclizations and
        // WM rearrangements are never reported as transannular (see formatMechanismStep.js).
        transannular = step.step_transannular === null || step.step_transannular === undefined || isTransannularIgnored(step.step_type) ? "" : (Number(step.step_transannular) === 1 ? "True" : "False")
        evidence = csvValue(step.mechanism_evidence)
        publications.add(step.publication_link)
        mechanism_marts_id = csvValue(step.mechanism_persistent_id)
    })
    enzymes_genbank.delete("")
    enzymes_genbank.delete(null)
    enzymes_uniprot.delete("")
    enzymes_uniprot.delete(null)
    substrate_names.delete("")
    substrate_names.delete(null)
    product_names.delete("")
    product_names.delete(null)
    substrate_smiles_set.delete("")
    substrate_smiles_set.delete(null)
    product_smiles_set.delete("")
    product_smiles_set.delete(null)
    substrate_stereochemistry_set.delete("")
    substrate_stereochemistry_set.delete(null)
    product_stereochemistry_set.delete("")
    product_stereochemistry_set.delete(null)
    publications.delete("")
    publications.delete(null)
    enzymes_uniprot = Array.from(enzymes_uniprot).join(";")
    enzymes_genbank = Array.from(enzymes_genbank).join(";")
    enzymes_marts = Array.from(enzymes_marts).join(";")
    substrate_names = Array.from(substrate_names).join(";")
    publications = Array.from(publications).join(";")
    let substrate_smiles = Array.from(substrate_smiles_set).join(";")
    let substrate_pers_id = Array.from(substrate_pers_id_set).join(";")
    let product_pers_id = Array.from(product_pers_id_set).join(";")
    product_names = Array.from(product_names).join(";")
    let product_smiles = Array.from(product_smiles_set).join(";")
    let substrate_stereochemistry = Array.from(substrate_stereochemistry_set).join(";")
    let product_stereochemistry = Array.from(product_stereochemistry_set).join(";")
    return `"${enzymes_uniprot}","${enzymes_genbank}","${enzymes_marts}","${substrate_smiles}","${substrate_names}","${substrate_pers_id}","${substrate_stereochemistry}","${product_smiles}","${product_names}","${product_pers_id}","${product_stereochemistry}","${reaction_type}","${distance}","${transannular}","${evidence}","${publications}","${mechanism_marts_id}"\n`
}


function mechanismDataToCsv(data){
    data = orderMechanismSteps(data);
    let res = "Enzyme_uniprot_ids,Enzyme_genbank_ids,Enzyme_marts_ids,Substrate_smiles,Substrate_name,Substrate_marts_id,Substrate_stereochemistry,Product_smiles,Product_name,Product_marts_id,Product_stereochemistry,Reaction_type,Distance,Transannular,Evidence,Publication,Mechanism_marts_id\n"
    if(data.length === 0) return res
    let prev_mech_id = data[0].mechanism_id
    let prev_mech_step_id = data[0].mechanism_step_id
    let same_mechanism = []
    data.forEach((element) => {
        if(element.mechanism_id == prev_mech_id && element.mechanism_step_id == prev_mech_step_id){
            same_mechanism.push(element)
        }
        else{
            res = res + stepGroupToCsvLine(same_mechanism)
            same_mechanism = [element]
            prev_mech_id = element.mechanism_id
            prev_mech_step_id = element.mechanism_step_id
        }
    });
    // The loop only emits a line when the group changes, so the final group is
    // still pending here. Without this flush the last mechanism step of the last
    // mechanism is silently dropped from every download.
    if(same_mechanism.length > 0){
        res = res + stepGroupToCsvLine(same_mechanism)
    }
    return res
}

export default mechanismDataToCsv
